import { addDays, duration, shortDay, weekDays } from "./week.ts";

/* ──────────────────────────────────────────────────────────────────────────
   Targets (Jay, 2026-11-05): a number of streams per day, week or month,
   and a time in hours per stream, day, week or month. One target for every
   creator, and an override for any creator who needs another.

   How the time reads with the number:
     per stream  a stream counts only if it lasts that long (the old rule:
                 3 streams a week, 3 hours each)
     per day     a stream counts only if its day adds up to that long
     per week    every stream counts, and the week must add up to that long
     per month   the same over the month
   A number per day means every day: each day must have that many streams.
   ────────────────────────────────────────────────────────────────────────── */

export const COUNT_PERIODS = ["day", "week", "month"] as const;
export const TIME_PERIODS = ["stream", "day", "week", "month"] as const;
export type CountPeriod = (typeof COUNT_PERIODS)[number];
export type TimePeriod = (typeof TIME_PERIODS)[number];

export type Target = { count: number; countPer: CountPeriod; hours: number; hoursPer: TimePeriod };

/** What the weekly report asked of every creator. */
export const DEFAULT_TARGET: Target = { count: 3, countPer: "week", hours: 3, hoursPer: "stream" };

/** The most hours a time can ask for in its period, so a typo can't ask for 300 hours a day. */
export const MAX_HOURS: Record<TimePeriod, number> = { stream: 24, day: 24, week: 168, month: 744 };
export const MAX_COUNT: Record<CountPeriod, number> = { day: 24, week: 100, month: 400 };

export type Status = "on" | "behind" | "none";

/** A stream: one run live, gaps of up to 15 minutes included, on the Melbourne day it started. */
export type Stream = { day: string; start: string; end: string; minutes: number; peak: number; avg: number; followersGained: number };

/** "3h", or "2h 30m" when it isn't whole hours. */
export const hoursText = (hours: number) => (Number.isInteger(hours) ? `${hours}h` : duration(Math.round(hours * 60)));
const per = (p: CountPeriod | TimePeriod) => (p === "day" ? "a day" : p === "week" ? "a week" : p === "month" ? "a month" : "a stream");

/** "3 streams a week, each at least 3h", in words for the editor and the page. */
export function describeTarget(t: Target): string {
  const count = `${t.count} ${t.count === 1 ? "stream" : "streams"} ${per(t.countPer)}`;
  if (!t.hours) return count;
  if (t.hoursPer === "stream") return `${count}, each at least ${hoursText(t.hours)}`;
  if (t.hoursPer === "day") return `${count}, on days with at least ${hoursText(t.hours)} live`;
  return `${count}, and ${hoursText(t.hours)} live ${per(t.hoursPer)}`;
}

const SHORT: Record<CountPeriod | TimePeriod, string> = { stream: "stream", day: "day", week: "wk", month: "mo" };

/** "3/wk, 3h/stream" for a narrow column. */
export function shortTarget(t: Target): string {
  const count = `${t.count}/${SHORT[t.countPer]}`;
  return t.hours ? `${count}, ${hoursText(t.hours)}/${SHORT[t.hoursPer]}` : count;
}

export const sameTarget = (a: Target, b: Target) => a.count === b.count && a.countPer === b.countPer && a.hours === b.hours && a.hoursPer === b.hoursPer;

export const monthStart = (day: string) => `${day.slice(0, 7)}-01`;
export function monthEnd(day: string): string {
  const [y, m] = day.split("-").map(Number) as [number, number];
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
}
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthName = (day: string) => MONTHS[Number(day.slice(5, 7)) - 1]!;
/** "Nov so far" while it runs, "Nov" once over, "Nov to Nov 8" for a past week inside it. */
const monthLabel = (anchor: string, today: string) =>
  anchor === today ? `${monthName(anchor)} so far` : anchor === monthEnd(anchor) ? monthName(anchor) : `${monthName(anchor)} to ${shortDay(anchor)}`;
const daysFrom = (from: string, to: string) => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1;

export type Progress = {
  status: Status;
  /** Streams that count toward the number, by start. */
  counted: string[];
  /** The number: done of need, over `label` ("this week", "Nov so far", "days"). */
  done: number;
  need: number;
  label: string;
  /** The time, when the target asks for a total over a week or month. */
  time: { minutes: number; need: number; label: string } | null;
  /** While the period runs: what is still needed, or that it can't be made. */
  needs: string | null;
  late: boolean;
};

/**
 * One creator's progress against a target, seen from one week. A week's
 * number reads that week; a month's reads the month the week ends in (to
 * today, while it runs); a day's reads each day of the week, from the day
 * tracking began. `today` is a Melbourne day.
 */
export function evaluate(target: Target, streams: Stream[], view: { week: string; today: string; trackedFrom: string }): Progress {
  const weekEnd = addDays(view.week, 6);
  const current = view.today >= view.week && view.today <= weekEnd;
  const anchor = current ? view.today : weekEnd;
  // A past week belongs to the month its Thursday is in, as calendars number weeks: Oct 26 to Nov 1
  // reads October, not one day of November.
  const thursday = addDays(view.week, 3);
  const monthDay = current ? view.today : monthEnd(thursday) < weekEnd ? monthEnd(thursday) : weekEnd;
  const minutesNeeded = Math.round(target.hours * 60);
  const dayTotal = new Map<string, number>();
  for (const s of streams) dayTotal.set(s.day, (dayTotal.get(s.day) ?? 0) + s.minutes);

  const counts = (s: Stream) =>
    !minutesNeeded || target.hoursPer === "week" || target.hoursPer === "month"
      ? true
      : target.hoursPer === "stream"
        ? s.minutes >= minutesNeeded
        : (dayTotal.get(s.day) ?? 0) >= minutesNeeded;
  const inRange = (from: string, to: string) => streams.filter((s) => s.day >= from && s.day <= to);

  let done: number;
  let need: number;
  let label: string;
  let counted: Stream[];
  let streamed: boolean;
  let needs: string | null = null;
  let late = false;

  if (target.countPer === "day") {
    const first = view.trackedFrom > view.week ? view.trackedFrom : view.week;
    const days = weekDays(view.week).filter((d) => d >= first && d <= anchor);
    counted = inRange(first, anchor).filter(counts);
    const metOn = (d: string) => counted.filter((s) => s.day === d).length >= target.count;
    const met = days.filter(metOn);
    // Today isn't missed until it's over.
    const judged = days.filter((d) => d !== view.today || metOn(d));
    done = met.length;
    need = judged.length;
    label = need === 1 ? "day" : "days";
    streamed = inRange(first, anchor).length > 0;
    if (anchor === view.today && !metOn(view.today)) {
      const today = counted.filter((s) => s.day === view.today).length;
      needs = `Needs ${target.count - today} today`;
    }
  } else {
    const end = target.countPer === "week" ? anchor : monthDay;
    const from = target.countPer === "week" ? view.week : monthStart(end);
    const to = target.countPer === "week" ? weekEnd : monthEnd(end);
    // Up to the day the view ends on: a past week reads its month as it stood then.
    const all = inRange(from, end);
    counted = all.filter(counts);
    done = counted.length;
    need = target.count;
    label = target.countPer === "week" ? (anchor === view.today ? "this week" : "that week") : monthLabel(end, view.today);
    streamed = all.length > 0;
    if (anchor === view.today && done < need) {
      const remaining = need - done;
      // A stream a day at most: today is spent once it has one.
      const left = daysFrom(view.today, to) - (streams.some((s) => s.day === view.today) ? 1 : 0);
      late = remaining > left;
      needs = late ? "Can't make it" : `Needs ${remaining} more`;
    }
  }

  let time: Progress["time"] = null;
  if (minutesNeeded && (target.hoursPer === "week" || target.hoursPer === "month")) {
    const end = target.hoursPer === "week" ? anchor : monthDay;
    const from = target.hoursPer === "week" ? view.week : monthStart(end);
    const minutes = inRange(from, end).reduce((sum, s) => sum + s.minutes, 0);
    time = { minutes, need: minutesNeeded, label: target.hoursPer === "week" ? (current ? "this week" : "that week") : monthLabel(end, view.today) };
    if (anchor === view.today && minutes < minutesNeeded) {
      const short = duration(minutesNeeded - minutes);
      needs = needs && needs !== "Can't make it" ? `${needs} and ${short}` : needs ?? `Needs ${short} more`;
    }
  }

  const met = done >= need && (!time || time.minutes >= time.need);
  return {
    // Met with nothing streamed is only a day target on a Monday morning: nothing judged yet.
    status: !streamed ? "none" : met ? "on" : "behind",
    counted: counted.map((s) => s.start),
    done,
    need,
    label,
    time,
    // A day target can be met so far and still need today's.
    needs: met && target.countPer !== "day" ? null : needs,
    late,
  };
}
